import React, { useRef, useCallback, useState, useEffect, useMemo } from 'react';
import css from './AgSlider.module.css';

export interface SliderMark {
  value: number;
  label?: string;
}

export interface AgSliderProps {
  value?: number | number[];
  defaultValue?: number | number[];
  /** À chaque déplacement (glisser, toucher, clavier) */
  onChange?: (event: Event, value: number | number[]) => void;
  /**
   * Quand le geste se termine (doigt ou pointeur relâché, toucher du rail,
   * touche clavier) : c'est le moment d'appliquer une action coûteuse — un
   * saut dans un document, un rechargement — que `onChange` déclencherait à
   * chaque pixel du glisser.
   */
  onChangeCommitted?: (value: number | number[]) => void;
  min?: number;
  max?: number;
  step?: number;
  marks?: boolean | SliderMark[];
  size?: 'sm' | 'md';
  disabled?: boolean;
  valueLabelDisplay?: 'auto' | 'on' | 'off';
  color?: 'primary' | 'secondary';
  className?: string;
  style?: React.CSSProperties;
  /** Nom accessible du curseur (`aria-label` du pouce) */
  'aria-label'?: string;
  /** Lecture humaine de la valeur pour les lecteurs d'écran (`aria-valuetext`), ex. « page 12 sur 348 » */
  getAriaValueText?: (value: number) => string;
}

/** Pas d'un saut clavier PageUp / PageDown, en nombre de `step`. */
const PAGE_STEPS = 10;

/**
 * Curseur de sélection de valeur numérique. Glisser au doigt ou à la souris
 * (Pointer Events, capture du pointeur), toucher du rail, flèches du clavier ;
 * `onChangeCommitted` à la fin du geste.
 * @category Formulaires
 */
export const AgSlider: React.FC<AgSliderProps> = ({
  value: controlledValue,
  defaultValue = 0,
  onChange,
  onChangeCommitted,
  min = 0,
  max = 100,
  step = 1,
  marks = false,
  size = 'md',
  disabled = false,
  valueLabelDisplay = 'off',
  color = 'primary',
  className,
  style,
  'aria-label': ariaLabel,
  getAriaValueText,
}) => {
  const trackRef = useRef<HTMLDivElement>(null);
  const [internalValue, setInternalValue] = useState<number | number[]>(
    controlledValue ?? defaultValue
  );
  const [dragging, setDragging] = useState<number | null>(null);
  const [hoveredThumb, setHoveredThumb] = useState<number | null>(null);

  const value = controlledValue ?? internalValue;
  const isRange = Array.isArray(value);
  const values = useMemo(() => (isRange ? (value as number[]) : [value as number]), [isRange, value]);

  // Dernière valeur posée pendant un geste : `onChangeCommitted` la lit au
  // relâchement, quand l'état React n'a pas encore forcément été rerendu.
  const latestRef = useRef<number | number[]>(value);
  latestRef.current = value;
  const onChangeCommittedRef = useRef(onChangeCommitted);
  onChangeCommittedRef.current = onChangeCommitted;

  useEffect(() => {
    if (controlledValue !== undefined) {
      setInternalValue(controlledValue);
    }
  }, [controlledValue]);

  const getPercent = (val: number) => ((val - min) / (max - min)) * 100;

  const clampToStep = useCallback(
    (raw: number) => {
      const stepped = Math.round((raw - min) / step) * step + min;
      return Math.max(min, Math.min(max, stepped));
    },
    [min, max, step]
  );

  const getValueFromPosition = useCallback(
    (clientX: number) => {
      if (!trackRef.current) return min;
      const rect = trackRef.current.getBoundingClientRect();
      if (rect.width <= 0) return min;
      const percent = Math.max(0, Math.min(1, (clientX - rect.left) / rect.width));
      return clampToStep(min + percent * (max - min));
    },
    [min, max, clampToStep]
  );

  const updateValue = useCallback(
    (newVal: number, thumbIndex: number) => {
      let nextValue: number | number[];
      if (isRange) {
        const arr = [...(value as number[])];
        arr[thumbIndex] = newVal;
        // Ensure range values don't cross
        if (thumbIndex === 0 && newVal > arr[1]) arr[0] = arr[1];
        if (thumbIndex === 1 && newVal < arr[0]) arr[1] = arr[0];
        nextValue = arr;
      } else {
        nextValue = newVal;
      }
      latestRef.current = nextValue;
      setInternalValue(nextValue);
      if (onChange) {
        const syntheticEvent = new Event('change');
        onChange(syntheticEvent, nextValue);
      }
      return nextValue;
    },
    [isRange, value, onChange]
  );

  const commit = useCallback(() => {
    onChangeCommittedRef.current?.(latestRef.current);
  }, []);

  const handlePointerDown = useCallback(
    (e: React.PointerEvent<HTMLDivElement>, thumbIndex: number) => {
      if (disabled) return;
      e.preventDefault();
      e.stopPropagation();
      const thumb = e.currentTarget;
      thumb.focus();
      // Capture : le glisser continue hors du pouce, et un doigt qui quitte
      // le rail ne fait pas défiler la page (avec `touch-action: none`).
      try { thumb.setPointerCapture?.(e.pointerId); } catch { /* pointeur déjà relâché */ }
      setDragging(thumbIndex);
    },
    [disabled]
  );

  useEffect(() => {
    if (dragging === null) return;
    const handleMove = (e: PointerEvent) => {
      updateValue(getValueFromPosition(e.clientX), dragging);
    };
    const handleUp = () => {
      setDragging(null);
      commit();
    };
    document.addEventListener('pointermove', handleMove);
    document.addEventListener('pointerup', handleUp);
    document.addEventListener('pointercancel', handleUp);
    return () => {
      document.removeEventListener('pointermove', handleMove);
      document.removeEventListener('pointerup', handleUp);
      document.removeEventListener('pointercancel', handleUp);
    };
  }, [dragging, getValueFromPosition, updateValue, commit]);

  const handleTrackPointerDown = useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      if (disabled) return;
      const newVal = getValueFromPosition(e.clientX);
      let thumbIndex = 0;
      if (isRange) {
        // Move the closest thumb
        const arr = value as number[];
        const dist0 = Math.abs(arr[0] - newVal);
        const dist1 = Math.abs(arr[1] - newVal);
        thumbIndex = dist0 <= dist1 ? 0 : 1;
      }
      updateValue(newVal, thumbIndex);
      // Le geste continue depuis le point touché : le pouce suit le doigt.
      setDragging(thumbIndex);
    },
    [disabled, getValueFromPosition, isRange, value, updateValue]
  );

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLDivElement>, thumbIndex: number) => {
      if (disabled) return;
      const current = values[thumbIndex];
      let next: number | null = null;
      switch (e.key) {
        case 'ArrowRight': case 'ArrowUp': next = current + step; break;
        case 'ArrowLeft': case 'ArrowDown': next = current - step; break;
        case 'PageUp': next = current + step * PAGE_STEPS; break;
        case 'PageDown': next = current - step * PAGE_STEPS; break;
        case 'Home': next = min; break;
        case 'End': next = max; break;
        default: return;
      }
      e.preventDefault();
      updateValue(clampToStep(next), thumbIndex);
      commit();
    },
    [disabled, values, step, min, max, updateValue, clampToStep, commit]
  );

  const markItems: SliderMark[] = Array.isArray(marks)
    ? marks
    : marks
      ? Array.from({ length: Math.floor((max - min) / step) + 1 }, (_, i) => ({
          value: min + i * step,
        }))
      : [];

  const trackStart = isRange ? getPercent((value as number[])[0]) : 0;
  const trackEnd = isRange ? getPercent((value as number[])[1]) : getPercent(value as number);

  const rootClasses = [
    css.root,
    css[`size-${size}`],
    css[`color-${color}`],
    disabled ? css.disabled : '',
    className || '',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <div className={rootClasses} style={style}>
      <div className={css.rail} ref={trackRef} onPointerDown={handleTrackPointerDown}>
        <div
          className={css.track}
          style={{ left: `${trackStart}%`, width: `${trackEnd - trackStart}%` }}
        />
        {markItems.map((mark) => (
          <div
            key={mark.value}
            className={css.markDot}
            style={{ left: `${getPercent(mark.value)}%` }}
          />
        ))}
        {values.map((val, i) => {
          const showLabel =
            valueLabelDisplay === 'on' ||
            (valueLabelDisplay === 'auto' && (dragging === i || hoveredThumb === i));
          return (
            <div
              key={i}
              className={`${css.thumb} ${dragging === i ? css.thumbActive : ''}`}
              style={{ left: `${getPercent(val)}%` }}
              onPointerDown={(e) => handlePointerDown(e, i)}
              onKeyDown={(e) => handleKeyDown(e, i)}
              onMouseEnter={() => setHoveredThumb(i)}
              onMouseLeave={() => setHoveredThumb(null)}
              role="slider"
              aria-label={ariaLabel}
              aria-valuemin={min}
              aria-valuemax={max}
              aria-valuenow={val}
              aria-valuetext={getAriaValueText ? getAriaValueText(val) : undefined}
              aria-disabled={disabled || undefined}
              tabIndex={disabled ? -1 : 0}
            >
              {showLabel && <span className={css.valueLabel}>{getAriaValueText ? getAriaValueText(val) : val}</span>}
            </div>
          );
        })}
      </div>
      {markItems.some((m) => m.label) && (
        <div className={css.marks}>
          {markItems.map((mark) =>
            mark.label ? (
              <span
                key={mark.value}
                className={css.markLabel}
                style={{ left: `${getPercent(mark.value)}%` }}
              >
                {mark.label}
              </span>
            ) : null
          )}
        </div>
      )}
    </div>
  );
};

export default AgSlider;
